// ---------------------------------------------------------------------------
// resolveLeadTreatmentPrice
//
// Single source of truth for the price cascade displayed in attended-popup
// price headers.  Shared by ConsultationAttendedAppointment and
// AttendedAppointment (both previously had inline copies).
//
// Stable treatment selection: filter by `filterStatus`, sort by `updated_at`
// DESC, then fall back to `lead_treatments[0]` — mirrors the pattern in
// `useLeadTableHelper.js` and `getLeadLatestData.js`.
//
// Price cascade (per-lead override first, then master defaults):
//   custom_price → treatment_data.price → treatment_item.price → null
//
// Consistent with TreatmentPlanCostTable.jsx:36, TreatmentAndAppointment.jsx:363.
// ---------------------------------------------------------------------------

/**
 * @param {object} opts
 * @param {object} [opts.followLead]          - Fresh server-side lead object (preferred)
 * @param {object} [opts.selectedTreatmentData] - Click-time treatment snapshot (fallback)
 * @param {string} [opts.filterStatus]        - Treatment status to prefer (default: CONSULTATION_ATTENDED)
 * @returns {number|null} The resolved price, or null if no price data found.
 */
export default function resolveLeadTreatmentPrice({
    followLead,
    selectedTreatmentData,
    filterStatus = 'CONSULTATION_ATTENDED',
} = {}) {
    const treatments = (followLead?.lead_treatments || []).filter((t) => t.status === filterStatus);
    const sorted = [...treatments].sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
    const lt = sorted[0] || followLead?.lead_treatments?.[0];
    const ltSelected = selectedTreatmentData?.treatmentData;

    // Prefer fresh followLead data over click-time selectedTreatmentData snapshot.
    const priceFromFollow = lt?.custom_price ?? lt?.treatment_data?.price ?? lt?.treatment_item?.price ?? null;
    const priceFromSelected = ltSelected?.custom_price ?? ltSelected?.price ?? null;
    return priceFromFollow ?? priceFromSelected ?? null;
}
